// Copy, paste and fill (design 02 12.1). The clipboard gets what the cells
// show as tab-separated text, the way Excel puts it there, so pasting into
// Excel, a chat or a memo gives the values. This page also keeps what was
// typed in the copied cells: pasting that same text here pastes the inputs,
// formulas moved by the distance as Excel does (relative references move,
// $absolute ones stay, the engine's translate). Text from elsewhere (Excel,
// a web page) is pasted as typed input: "=..." becomes a formula.
import type { CellEdit, Style } from './model'
import { parseTsv, toTsv } from './tsv'

export interface Copied {
  /** the text put on the clipboard */
  tsv: string
  /** the copied cells' inputs and styles, rows of cells */
  inputs: string[][]
  styles: (Style | null)[][]
  /** where they were copied from */
  row: number
  col: number
  sheetId: number
}

let kept: Copied | null = null

export function keepCopied(c: Copied | null): void {
  kept = c
}

export function copied(): Copied | null {
  return kept
}

/** Builds what a copy keeps from the shown texts, inputs and styles of a block. */
export function makeCopied(
  sheetId: number,
  row: number,
  col: number,
  texts: string[][],
  inputs: string[][],
  styles: (Style | null)[][],
): Copied {
  return { tsv: toTsv(texts), inputs, styles, row, col, sheetId }
}

type Translate = (items: { formula: string; drow: number; dcol: number }[]) => Promise<(string | null)[]>

/** Formulas moved by (drow, dcol) in one go (one that does not parse stays as it is). */
async function moveFormulas(inputs: string[][], drow: number, dcol: number, translate: Translate): Promise<string[][]> {
  const out = inputs.map((r) => [...r])
  if (drow === 0 && dcol === 0) return out
  const at: [number, number][] = []
  const items: { formula: string; drow: number; dcol: number }[] = []
  out.forEach((r, i) =>
    r.forEach((v, j) => {
      if (v.startsWith('=')) {
        at.push([i, j])
        items.push({ formula: v, drow, dcol })
      }
    }),
  )
  if (items.length === 0) return out
  const moved = await translate(items)
  at.forEach(([i, j], k) => {
    const m = moved[k]
    out[i]![j] = m ?? out[i]![j]!
  })
  return out
}

/**
 * The edits of a paste at (row, col). When the text is what this page
 * copied, the inputs (and styles) are pasted with formulas moved; else the
 * text's cells are typed in. One cell pasted into a bigger selection fills
 * all of it (rect).
 */
export async function pasteEdits(
  text: string,
  row: number,
  col: number,
  translate: Translate,
  fill?: { r2: number; c2: number },
): Promise<CellEdit[]> {
  const mine = kept && normalize(kept.tsv) === normalize(text) ? kept : null
  let inputs: string[][]
  let styles: (Style | null)[][] | null = null
  if (mine) {
    inputs = mine.inputs
    styles = mine.styles
  } else {
    inputs = parseTsv(text)
  }
  if (inputs.length === 0) return []
  const edits: CellEdit[] = []
  const single = inputs.length === 1 && inputs[0]!.length === 1
  if (single && fill && (fill.r2 > row || fill.c2 > col)) {
    // one cell into a selection: every cell of it, formulas moved for each
    const src = inputs[0]![0]!
    const items: { formula: string; drow: number; dcol: number }[] = []
    const cells: [number, number][] = []
    for (let r = row; r <= fill.r2; r++)
      for (let c = col; c <= fill.c2; c++) {
        cells.push([r, c])
        if (src.startsWith('='))
          items.push({ formula: src, drow: r - (mine ? mine.row : row), dcol: c - (mine ? mine.col : col) })
      }
    const moved = src.startsWith('=') ? await translate(items) : []
    cells.forEach(([r, c], k) => {
      const input = src.startsWith('=') ? (moved[k] ?? src) : src
      edits.push({ row: r, col: c, input, ...(styles ? { style: styles[0]?.[0] ?? null } : {}) })
    })
    return edits
  }
  const moved = mine ? await moveFormulas(inputs, row - mine.row, col - mine.col, translate) : inputs
  moved.forEach((r, i) =>
    r.forEach((input, j) => {
      const e: CellEdit = { row: row + i, col: col + j, input }
      if (styles) e.style = styles[i]?.[j] ?? null
      edits.push(e)
    }),
  )
  return edits
}

function normalize(s: string): string {
  return s.replace(/\r\n/g, '\n').replace(/\n$/, '')
}

/** Ctrl+D: the top row of the block copied down to the rows under it. */
export async function fillDownEdits(
  top: string[],
  row: number,
  col: number,
  lastRow: number,
  translate: Translate,
): Promise<CellEdit[]> {
  const edits: CellEdit[] = []
  const items: { formula: string; drow: number; dcol: number }[] = []
  const at: number[] = []
  for (let r = row + 1; r <= lastRow; r++)
    top.forEach((input, j) => {
      edits.push({ row: r, col: col + j, input })
      if (input.startsWith('=')) {
        at.push(edits.length - 1)
        items.push({ formula: input, drow: r - row, dcol: 0 })
      }
    })
  if (items.length) {
    const moved = await translate(items)
    at.forEach((i, k) => {
      edits[i]!.input = moved[k] ?? edits[i]!.input
    })
  }
  return edits
}
